/**
 * Cognara 4D Linkage — scope switcher.
 *
 * One console, five scopes: the macro schedule and each discipline schedule
 * (Engineering, Procurement, Fabrication & Construction, Pre-commissioning &
 * Commissioning). Macro linking drives the timeliner; discipline linking drives
 * the KPI overlays.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Layers } from 'lucide-react';
import { CognaraLinkHitl } from './CognaraLinkHitl';
import { DisciplineLinkHitl } from './DisciplineLinkHitl';

const SCOPES = [
  { key: 'MACRO', label: 'Macro schedule', colour: '#60A5FA' },
  { key: 'ENGINEERING', label: 'Engineering', colour: '#38BDF8' },
  { key: 'PROCUREMENT', label: 'Procurement', colour: '#A78BFA' },
  { key: 'FC', label: 'Fabrication & Construction', colour: '#FB923C' },
  { key: 'PRECOMM_COMM', label: 'Pre-commissioning & Commissioning', colour: '#34D399' },
];

export function FourDLinkageHitl({ projectId, modelId, scheduleId, viewerRef, canEdit,
                                   onClose, onCommitted }) {
  const [scope, setScope] = useState('MACRO');
  const current = SCOPES.find(s => s.key === scope) || SCOPES[0];

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="fourd-linkage-hitl">
      <div className="flex items-center gap-1 px-3 h-8 border-b shrink-0 bg-muted/30">
        <Layers className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Linkage scope</span>
        {SCOPES.map(s => (
          <button key={s.key} onClick={() => setScope(s.key)}
                  className={`px-2 py-0.5 rounded-md text-[11px] transition-colors border ${
                    scope === s.key ? 'bg-muted text-foreground' : 'border-transparent text-muted-foreground hover:bg-muted/50'}`}
                  style={{ borderColor: scope === s.key ? s.colour : 'transparent' }}
                  data-testid={`hitl-scope-${s.key}`}>
            {s.label}
          </button>
        ))}
        {scope !== 'MACRO' && (
          <Badge variant="outline" className="text-[9px] h-4 px-1 ml-auto" data-testid="hitl-scope-note">
            Drives the KPI overlays
          </Badge>
        )}
      </div>

      <div className="flex-1 min-h-0">
        {scope === 'MACRO' ? (
          <CognaraLinkHitl
            projectId={projectId}
            modelId={modelId}
            viewerRef={viewerRef}
            canEdit={canEdit}
            onClose={onClose}
            onCommitted={onCommitted}
          />
        ) : !scheduleId ? (
          <div className="p-6 text-xs text-muted-foreground" data-testid="hitl-no-schedule">
            Link a schedule to this model first — the discipline registers are resolved through it.
          </div>
        ) : (
          <DisciplineLinkHitl
            key={scope}
            projectId={projectId}
            modelId={modelId}
            scheduleId={scheduleId}
            discipline={scope}
            label={current.label}
            viewerRef={viewerRef}
            canEdit={canEdit}
            onClose={onClose}
            onLinksChanged={onCommitted}
          />
        )}
      </div>
    </div>
  );
}

export default FourDLinkageHitl;
